import React, { useContext, useEffect, useState } from 'react';
import { NextPageContext } from 'next';
import { addDays, differenceInDays } from 'date-fns';
import { CancelTokenSource } from 'axios';
import shuffle from 'lodash/shuffle';
import classNames from 'classnames';
import { Calendar, Car, TicketCheck } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import SwipeableViews from 'react-swipeable-views';
import { fetchMemory } from '../integrations/contentful/queries';
import { CmsAsset, CmsMemory } from '../integrations/contentful/types';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import MemoryTitle from '../components/memories/MemoryTitle';
import Container from '../components/grid/Container';
import { baseXUnit, color, font, fontSize } from '../styles/theme';
import MemoryToC from '../components/memories/MemoryToC';
import MemoryPhases from '../components/memories/MemoryPhases';
import Spacing from '../components/common/Spacing';
import BackButton from '../components/navigation/BackButton';
import { t } from '../utils/i18n';
import { navbarLayout } from '../layouts/navbarLayout';
import Breadcrumbs from '../components/head/Breadcrumbs';
import RouteLink from '../components/navigation/RouteLink';
import HyperLink from '../components/elements/HyperLink';
import {
  fetchTripPhaseCities,
  fetchTripReservingDetails
} from '../integrations/erp/queries';
import {
  IMTTripPhaseCities,
  IMTTripReservingDetails
} from '../integrations/erp/apiTypes';
import getUseEffectRequestWithCleanup from '../utils/getUseEffectRequestWithCleanup';
import PriceBox from '../components/common/sticky-pricebox/PriceBox';
import StickyBottomBox from '../components/common/sticky-pricebox/StickyBottomBox';
import TravelInfoBox from '../components/common/sticky-pricebox/TravelInfoBox';
import MediaGalleryModal, {
  MediaGalleryItem
} from '../components/media-gallery/MediaGalleryModal';
import { determineIconForMemoryPhase } from '../utils/memories';
import DivButton from '../components/a11y/DivButton';
import Icon from '../components/common/Icon';
import { arrayHead } from '../utils/array';
import Row from '../components/grid/Row';
import Col from '../components/grid/Col';
import CmsMediaFixedHeight from '../components/cms/CmsMediaFixedHeight';
import DisplayOn from '../components/common/DisplayOn';
import HorizontalLine from '../components/common/HorizontalLine';
import H from '../components/elements/H';
import HorizontalCardList, {
  VisibleCards
} from '../components/common/HorizontalCardList';
import {
  DesktopHorizontalList,
  getDesktopHorizontalListTopControls
} from '../components/common/DesktopHorizontalList';
import { fetchMemories } from '../integrations/contentful/queries';
import MemoryCard from '../components/memories/MemoryCard';
import { LoadingState } from '../loading/loadable';
import RenderAsync from '../components/common/RenderAsync';
import { trackAnalyticsEvent } from '../analytics/analytics';
import { configuration } from '../utils/environment';
import { MemoryHotelTab } from '../components/memories/MemoryHotelTab';
import { memoryListPageBreadcrumbItem } from './MemoryListPage';
import { TimPage } from './TimPage';
import { SearchContext } from './_app';

interface MemoryPageProps {
  memory?: CmsMemory;
  imtTripId?: number;
  imtTripPhaseCities: IMTTripPhaseCities[];
}

interface MediaGalleryState {
  show: boolean;
  initialSlideIndex: number;
}

async function fetchTripReservingDetailsForMemory(
  imtTripId: number,
  memory: CmsMemory,
  source: CancelTokenSource
): Promise<IMTTripReservingDetails | undefined> {
  const searchStartDate = addDays(new Date(), 1);
  const searchEndDate = addDays(searchStartDate, 7);
  const memoryDuration =
    differenceInDays(
      new Date(memory.fields.endDate),
      new Date(memory.fields.startDate)
    ) + 1; // +1 to get the duration in whole days instead of just the difference

  return fetchTripReservingDetails(
    imtTripId,
    {
      searchStartDate,
      searchEndDate,
      prioritizedDuration: memoryDuration,
      ownCarRequired: memory.fields.trip?.fields.ownCarRequired
    },
    source
  );
}

interface RelatedMemories {
  relatedMemories: CmsMemory[];
}

const priceBoxHeight = 9;
export const MEMORY_PAGE_ANALYTICS_CATEGORY = 'Matkasivu';

interface RelatedMemories {
  relatedMemories: CmsMemory[];
}

interface GalleryHeroProps {
  items: MediaGalleryItem[];
  onMediaClick: (media: CmsAsset) => void;
}

const GalleryHero: React.FunctionComponent<GalleryHeroProps> = ({
  items,
  onMediaClick
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (items.length === 0) {
    return null;
  }

  const mainImage = items[activeIndex]?.media || items[0].media;

  return (
    <div className="gallery-hero">
      <DisplayOn large>
        <div className="desktop-gallery">
          <div className="thumbnails">
            {items.map((item, index) => (
              <DivButton
                key={index}
                className={classNames('thumbnail-wrapper', {
                  active: index === activeIndex
                })}
                onClick={() => setActiveIndex(index)}
              >
                <CmsMediaFixedHeight
                  media={item.media}
                  fixedHeight={11}
                  borderRadius={0.5}
                />
              </DivButton>
            ))}
          </div>
          <div className="main-image-container">
            <DivButton
              onClick={() => onMediaClick(mainImage)}
              data-cy="gallery-main-image"
            >
              <div className="main-media-wrapper">
                <CmsMediaFixedHeight
                  media={mainImage}
                  fixedHeight={62}
                  borderRadius={1}
                />
                <div className="media-button">
                  <Icon name="nav-grid" size={2} color="dark" />
                  <Spacing left={1.5}>
                    <span className="button-text">{t('Kaikki media')}</span>
                  </Spacing>
                </div>
              </div>
            </DivButton>
          </div>
        </div>
      </DisplayOn>

      <DisplayOn small medium>
        <div className="mobile-gallery">
          <SwipeableViews
            index={activeIndex}
            onChangeIndex={(index) => setActiveIndex(index)}
            enableMouseEvents
            resistance
          >
            {items.map((item, index) => (
              <DivButton key={index} onClick={() => onMediaClick(item.media)}>
                <CmsMediaFixedHeight
                  media={item.media}
                  fixedHeight={[58, 58]}
                  borderRadius={1}
                />
              </DivButton>
            ))}
          </SwipeableViews>
          {items.length > 1 && (
            <div className="carousel-dots">
              {items.map((_, index) => (
                <DivButton
                  key={index}
                  className={classNames('dot', {
                    active: index === activeIndex
                  })}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </div>
          )}
          <div className="mobile-media-button-container">
            <DivButton
              className="media-button"
              onClick={() => onMediaClick(mainImage)}
            >
              <Icon name="nav-grid" size={2} color="dark" />
              <Spacing left={1.5}>
                <span className="button-text">{t('Kaikki media')}</span>
              </Spacing>
            </DivButton>
          </div>
        </div>
      </DisplayOn>

      <style jsx>{`
        .gallery-hero {
          width: 100%;
        }
        .desktop-gallery {
          display: flex;
          gap: ${baseXUnit(2)};
        }
        .thumbnails {
          display: flex;
          flex-direction: column;
          gap: ${baseXUnit(1)};
          width: ${baseXUnit(12)};
          max-height: ${baseXUnit(62)};
          overflow-y: auto;
          /* Hide scrollbar */
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .thumbnails::-webkit-scrollbar {
          display: none;
        }
        .thumbnail-wrapper {
          cursor: pointer;
          opacity: 0.6;
          transition: 0.2s;
          border: 2px solid transparent;
          border-radius: ${baseXUnit(1)};
          overflow: hidden;
        }
        .thumbnail-wrapper.active {
          opacity: 1;
          border-color: ${color.brand};
        }
        .thumbnail-wrapper:hover {
          opacity: 1;
        }
        .main-image-container {
          flex: 1;
          position: relative;
        }
        .main-media-wrapper {
          position: relative;
          border-radius: ${baseXUnit(1)};
          overflow: hidden;
        }
        .media-button {
          position: absolute;
          left: ${baseXUnit(3)};
          bottom: ${baseXUnit(3)};
          background: ${color.white};
          padding: ${baseXUnit(1)} ${baseXUnit(2.5)};
          border-radius: ${baseXUnit(4)};
          display: flex;
          align-items: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          z-index: 1;
          transition:
            transform 0.2s,
            background-color 0.2s;
        }
        .button-text {
          font-family: ${font.primary};
          font-weight: bold;
          font-size: ${fontSize.px14};
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .media-button:hover {
          transform: translateY(-2px);
          background-color: ${color.lightGray};
        }
        .mobile-gallery {
          position: relative;
        }
        .carousel-dots {
          display: flex;
          justify-content: center;
          gap: ${baseXUnit(1.5)};
          margin-top: ${baseXUnit(2)};
        }
        .mobile-gallery :global(.dot) {
          width: ${baseXUnit(1)};
          height: ${baseXUnit(1)};
          border-radius: 50%;
          background: ${color.muted};
          cursor: pointer;
          transition:
            background-color 0.2s,
            transform 0.2s;
        }
        .mobile-gallery :global(.dot.active) {
          background: ${color.brand};
          transform: scale(1.2);
        }
        .mobile-media-button-container {
          display: flex;
          justify-content: center;
          margin-top: ${baseXUnit(3)};
        }
        @media screen and (max-width: 768px) {
          .mobile-media-button-container .media-button {
            position: static;
            box-shadow: none;
            border: 1px solid ${color.lightGray};
          }
        }
      `}</style>
    </div>
  );
};

const RelatedMemories: React.FunctionComponent<RelatedMemories> = ({
  relatedMemories
}) => {
  function renderMemoryCard(memory: CmsMemory) {
    const traveller = arrayHead(memory.fields.travellers);
    if (!traveller) {
      return null;
    }
    return (
      <MemoryCard
        key={memory.sys.id}
        memory={{
          ...memory.fields,
          mainImage: memory.fields.mainImage
        }}
        traveller={{
          name: traveller.fields.title,
          profilePicture: traveller.fields.profilePicture,
          slug: traveller.fields.slug
        }}
        data-testid="related-memory"
      />
    );
  }
  return (
    <div className="related-memories" data-testid="related-memories">
      <Spacing top={[8, 18, 18]} bottom={[11, 10, 10]}>
        <HorizontalLine expand={false} />
      </Spacing>
      <Container>
        <DisplayOn small medium>
          <Spacing bottom={[5, 4.5]}>
            <H styling="h2">
              <strong>{t('Lisää matkoja sinulle')}</strong>
            </H>
          </Spacing>
        </DisplayOn>
        <DisplayOn small>
          <Row>
            <Col size={24}>
              <HorizontalCardList visibleCards={VisibleCards.One} expand>
                {relatedMemories.map(renderMemoryCard)}
              </HorizontalCardList>
            </Col>
          </Row>
        </DisplayOn>
        <DisplayOn medium>
          <Row>
            <Col size={24}>
              <HorizontalCardList visibleCards={VisibleCards.Two} expand>
                {relatedMemories.map(renderMemoryCard)}
              </HorizontalCardList>
            </Col>
          </Row>
        </DisplayOn>
        <DisplayOn large>
          <DesktopHorizontalList
            elementsPerSlide={3}
            controls={getDesktopHorizontalListTopControls(
              <H styling="h2">
                <strong>{t('Lisää matkoja sinulle')}</strong>
              </H>
            )}
          >
            {relatedMemories.map((memory) => {
              return (
                <Spacing key={memory.sys.id} bottom={4}>
                  {renderMemoryCard(memory)}
                </Spacing>
              );
            })}
          </DesktopHorizontalList>
        </DisplayOn>
      </Container>
    </div>
  );
};

const MemoryPage: TimPage<MemoryPageProps> = ({
  memory,
  imtTripId,
  imtTripPhaseCities
}) => {
  const [mediaGalleryState, setMediaGalleryState] =
    React.useState<MediaGalleryState>({
      show: false,
      initialSlideIndex: 0
    });
  const [activeTab, setActiveTab] = useState<'esittely' | 'hotellit'>(
    'esittely'
  );
  const [tripReservationData, setTripReservationData] = React.useState<
    IMTTripReservingDetails | undefined
  >(undefined);
  const [tripReservationDataLoadingState, setTripReservationDataLoadingState] =
    useState<LoadingState>(LoadingState.STARTED);
  useEffect(() => {
    return getUseEffectRequestWithCleanup(async (source: CancelTokenSource) => {
      if (imtTripId && memory) {
        const reservationData = await fetchTripReservingDetailsForMemory(
          imtTripId,
          memory,
          source
        );
        setTripReservationDataLoadingState(
          reservationData ? LoadingState.DONE : LoadingState.FAILED
        );

        setTripReservationData(reservationData);
      } else {
        setTripReservationDataLoadingState(LoadingState.FAILED);
      }
    });
  }, [imtTripId, memory]);

  const shipTrip = imtTripId
    ? configuration.erpShipTripIds.includes(imtTripId)
    : false;

  const [tripDetailsBoxVisibility, setTripDetailsBoxVisibility] =
    React.useState<boolean>(false);

  const searchContext = useContext(SearchContext);

  const [relatedMemoriesLoadingState, setRelatedMemoriesLoadingState] =
    useState<LoadingState>(LoadingState.STARTED);

  const [relatedMemories, setRelatedMemories] = useState<CmsMemory[]>([]);
  useEffect(() => {
    let isCancelled = false;
    const maxRelatedMemories = 9;

    (async () => {
      /* We could do this also server side in getInitialProps, but let's optimze Time to first byte. */
      const searchAnswers = searchContext.answers.getAll();

      // It would be nice to fetch memories with the same tags when there are no search answers.
      // This would give more relevant related memories to user.
      const relatedMemories = (await fetchMemories(searchAnswers))
        .filter((cmsMemory) => {
          if (!memory) {
            return false;
          }
          // Remove current memory from related memories.
          return cmsMemory.sys.id !== memory.sys.id;
        })
        .slice(0, maxRelatedMemories);
      if (!isCancelled) {
        setRelatedMemories(shuffle(relatedMemories));
        setRelatedMemoriesLoadingState(LoadingState.DONE);
      }
    })();

    // Prevent state change on unmount to prevent memory leaks
    return () => {
      isCancelled = false;
    };
  }, [memory, searchContext.answers]);

  if (!memory) {
    return null;
  }

  function onPriceBoxInfoButtonClick() {
    setTripDetailsBoxVisibility(!tripDetailsBoxVisibility);
    trackAnalyticsEvent(
      MEMORY_PAGE_ANALYTICS_CATEGORY,
      'Avattu matkan lisätiedot varauspainikkeesta',
      memory ? memory.fields.title : ''
    );
  }

  const { phases } = memory.fields;
  const destination = arrayHead(memory.fields.destinations);

  const phaseGalleryItems: MediaGalleryItem[] = phases.reduce(
    (allMedia: MediaGalleryItem[], phase, phaseIndex) => {
      const media = [];
      if (phase.fields.mainMedia) {
        media.push(phase.fields.mainMedia);
      }
      media.push(...(phase.fields.media || []));
      const phaseGalleryItems = media.map((media) => ({
        phase: {
          iconName: determineIconForMemoryPhase(
            phase,
            phaseIndex === 0,
            shipTrip
          ),
          title: phase.fields.label,
          scrollTarget: phase.sys.id
        },
        media
      }));
      return allMedia.concat(phaseGalleryItems);
    },
    []
  );
  const mediaGalleryItems: MediaGalleryItem[] = [];
  if (memory.fields.mainImage) {
    mediaGalleryItems.push({
      media: memory.fields.mainImage
    });
  }
  mediaGalleryItems.push(...phaseGalleryItems);
  const canonicalPath = generatePath(Paths.Memory, {
    slug: memory.fields.slug
  });

  const displayMediaInMediaGallery = (media: CmsAsset) => {
    const initialSlideIndex = mediaGalleryItems.findIndex(
      (item) => item.media === media
    );
    if (initialSlideIndex >= 0) {
      setMediaGalleryState({ show: true, initialSlideIndex });
      trackAnalyticsEvent(
        MEMORY_PAGE_ANALYTICS_CATEGORY,
        'Avattu mediagalleriamodaali',
        media.fields.title
      );
    }
  };

  const renderBasicInfo = () => (
    <>
      <Spacing top={[2, 2, 2]} bottom={[6, 6, 10]}>
        {destination && (
          <MemoryTitle
            title={memory.fields.title}
            startDate={memory.fields.startDate}
            endDate={memory.fields.endDate}
            destination={destination}
          />
        )}
      </Spacing>
      <Spacing bottom={[6, 6, 11]} right={[0, 8]}>
        {memory.fields.matkanKesto && (
          <Spacing bottom={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} color={color.purple} strokeWidth={2} />
              <H styling="h5" color="gray">
                {t('Ajankohta')}
              </H>
            </div>
            <div style={{ marginTop: '4px' }}>
              <H styling="h4">
                <strong>
                  {memory.fields.matkanKesto}
                  {memory.fields.tripDepartures
                    ? `, ${memory.fields.tripDepartures}`
                    : ''}
                </strong>
              </H>
            </div>
          </Spacing>
        )}
        {memory.fields.destinations &&
          memory.fields.destinations.length > 0 && (
            <div className="golf-courses">
              <Spacing bottom={2}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <TicketCheck size={18} color={color.purple} strokeWidth={2} />
                  <H styling="h5" color="gray">
                    {memory.fields.golfInfo || t('2 kierrosta golfia')}
                  </H>
                </div>
              </Spacing>
              <Spacing bottom={6}>
                {memory.fields.destinations.map((dest, index) => (
                  <div key={dest.sys.id}>
                    <RouteLink
                      path={Paths.Destination}
                      params={{ slug: dest.fields.slug }}
                    >
                      <HyperLink>
                        <H styling="h4">
                          <strong>{dest.fields.title}</strong>
                        </H>
                      </HyperLink>
                    </RouteLink>
                    {index < memory.fields.destinations.length - 1 && (
                      <Spacing bottom={1} />
                    )}
                  </div>
                ))}
              </Spacing>
            </div>
          )}

        {memory.fields.vehicleInfo && (
          <Spacing bottom={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Car size={18} color={color.purple} strokeWidth={2} />
              <H styling="h5" color="gray">
                {t('Matkatapa')}
              </H>
            </div>
            <div style={{ marginTop: '4px' }}>
              <H styling="h4">
                <strong>{memory.fields.vehicleInfo}</strong>
              </H>
            </div>
          </Spacing>
        )}
      </Spacing>
    </>
  );

  return (
    <div data-cy="memory-page">
      <SEO
        title={(memory.fields.seoTitle || memory.fields.title) + ' - Matka'}
        canonicalPath={canonicalPath}
        description={
          memory.fields.seoDescription || memory.fields.leadParagraph
        }
        mainImage={memory.fields.mainImage}
      />
      <Breadcrumbs
        items={[
          memoryListPageBreadcrumbItem,
          { name: memory.fields.title, path: canonicalPath }
        ]}
      />

      <Spacing bottom={6}>
        <Container>
          <Row>
            <Col size={[24, 24, 15]}>
              <Spacing top={[0, 2, 2, 2]} bottom={3}>
                <GalleryHero
                  items={mediaGalleryItems}
                  onMediaClick={displayMediaInMediaGallery}
                />
              </Spacing>
              <DisplayOn small medium>
                {renderBasicInfo()}
              </DisplayOn>
              <Spacing top={4} bottom={4}>
                <div className="memory-tabs">
                  <button
                    className={`tab-button ${activeTab === 'esittely' ? 'active' : ''}`}
                    onClick={() => setActiveTab('esittely')}
                  >
                    Esittely
                  </button>
                  {memory.fields.hotelIds && (
                    <button
                      className={`tab-button ${activeTab === 'hotellit' ? 'active' : ''}`}
                      onClick={() => setActiveTab('hotellit')}
                    >
                      Hotellit
                    </button>
                  )}
                </div>
              </Spacing>

              {activeTab === 'esittely' && memory.fields.leadParagraph && (
                <Spacing bottom={6}>
                  <div className="lead-markdown">
                    <ReactMarkdown>{memory.fields.leadParagraph}</ReactMarkdown>
                  </div>
                </Spacing>
              )}

              {activeTab === 'hotellit' && memory.fields.hotelIds && (
                <Spacing bottom={6}>
                  <MemoryHotelTab
                    hotelIds={
                      Array.isArray(memory.fields.hotelIds)
                        ? memory.fields.hotelIds
                        : memory.fields.hotelIds
                            .split(',')
                            .map((s: string) => s.trim())
                            .filter((s: string) => s)
                    }
                  />
                </Spacing>
              )}
            </Col>
            <Col size={[24, 24, 9]}>
              <DisplayOn large>{renderBasicInfo()}</DisplayOn>
            </Col>
          </Row>
          <Row>
            <Col size={24}>
              <DisplayOn small medium>
                <HorizontalLine />
              </DisplayOn>
            </Col>
          </Row>
        </Container>
        <DisplayOn large>
          <HorizontalLine />
        </DisplayOn>

        {activeTab === 'esittely' && (
          <>
            <Container>
              <Spacing top={2}>
                <Row>
                  <Col size={[24, 24, 14]}>
                    <Spacing bottom={6}>
                      <H styling="h2">
                        <strong>{t('Matkan eri vaiheet')}</strong>
                      </H>
                    </Spacing>
                    <Spacing bottom={8}>
                      <MemoryToC phases={phases} shipTrip={shipTrip} />
                    </Spacing>
                  </Col>
                </Row>
                <MemoryPhases
                  phases={phases}
                  onMediaClick={displayMediaInMediaGallery}
                  shipTrip={shipTrip}
                />
              </Spacing>
            </Container>
            <RenderAsync
              state={relatedMemoriesLoadingState}
              /* This element is in the bottom of the page so we don't really need skeleton. */
              onLoading={() => <></>}
            >
              {relatedMemories.length > 0 && (
                <RelatedMemories relatedMemories={relatedMemories} />
              )}
            </RenderAsync>
          </>
        )}
      </Spacing>
      <Spacing bottom={14}>
        <StickyBottomBox>
          {tripReservationData && (
            <TravelInfoBox
              visible={tripDetailsBoxVisibility}
              imtTripPhaseCities={imtTripPhaseCities}
              availabilityDate={tripReservationData.date}
              shipTrip={shipTrip}
            />
          )}
          <PriceBox
            tripReservationData={tripReservationData}
            tripReservationDataLoadingState={tripReservationDataLoadingState}
            infoButtonOnClick={onPriceBoxInfoButtonClick}
            analyticsCategory={MEMORY_PAGE_ANALYTICS_CATEGORY}
            analyticsLabel={memory.fields.title}
            contentfulEntry={memory}
          />
        </StickyBottomBox>
      </Spacing>
      <MediaGalleryModal
        initialSlideIndex={mediaGalleryState.initialSlideIndex}
        items={mediaGalleryItems}
        closeDialog={() =>
          setMediaGalleryState({ ...mediaGalleryState, show: false })
        }
        showDialog={mediaGalleryState.show}
      />
      {/*language=CSS*/}
      <style jsx>{`
        .memory-tabs {
          display: flex;
          gap: ${baseXUnit(2)};
          align-items: center;
        }
        .tab-button {
          background: none;
          border: none;
          padding: ${baseXUnit(1.5)} ${baseXUnit(3)};
          font-family: ${font.primary};
          font-weight: bold;
          font-size: ${fontSize.px18};
          color: ${color.gray};
          cursor: pointer;
          position: relative;
          text-transform: uppercase;
          transition:
            background-color 0.2s,
            color 0.2s;
          border-radius: ${baseXUnit(4)};
        }
        .tab-button.active {
          color: ${color.white};
          background-color: ${color.coral};
        }
        .main-media-wrapper {
          overflow: hidden;
          border-radius: ${baseXUnit(1)};
          position: relative;
          cursor: pointer;
        }
        .lead-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .lead-list-item {
          display: flex;
          align-items: flex-start;
          gap: ${baseXUnit(2)};
          padding: ${baseXUnit(1.5)} 0;
          border-bottom: 1px solid ${color.lightGray};
          font-size: ${fontSize.px16};
          line-height: 1.5;
        }
        .lead-list-item:first-child {
          border-top: 1px solid ${color.lightGray};
        }
        .lead-list-item::before {
          content: '-';
          color: ${color.brand};
          font-weight: bold;
          flex-shrink: 0;
          margin-top: 1px;
        }
        :global(.lead-markdown ul) {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        :global(.lead-markdown ul li) {
          padding: ${baseXUnit(1)} 0;
          font-size: ${fontSize.px16};
          line-height: 1.5;
          padding-left: ${baseXUnit(3)};
          position: relative;
        }
        :global(.lead-markdown ul li::before) {
          content: '';
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: ${color.brand};
          position: absolute;
          left: 0;
          top: ${baseXUnit(2.5)};
          flex-shrink: 0;
        }
        :global(.lead-markdown p) {
          font-size: ${fontSize.px16};
          line-height: 1.6;
          margin: 0 0 ${baseXUnit(1.5)} 0;
        }
        :global(.lead-markdown p:last-child) {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

MemoryPage.getLayout = (page) =>
  navbarLayout(
    <BackButton defaultTitle={t('Matkat')} defaultPath={Paths.MemoryList} />,
    page,
    [priceBoxHeight, priceBoxHeight, 0] // Add extra space to footer in small & medium.
  );

MemoryPage.getInitialProps = async function ({ query }: NextPageContext) {
  const memorySlug = String(query.slug);
  const memory = await fetchMemory(memorySlug);
  let imtTripId = undefined;
  let imtTripPhaseCities: IMTTripPhaseCities[] = [];
  if (memory && memory.fields.trip) {
    imtTripId = memory.fields.trip.fields.erpTripId;
    imtTripPhaseCities = await fetchTripPhaseCities(imtTripId);
  }

  const httpErrorCode = !memory ? 404 : undefined;
  return { memory, imtTripId, imtTripPhaseCities, httpErrorCode };
};

export default MemoryPage;
